/**
 * custom-carousel.js
 *
 * Initialises Swiper for every custom carousel on the page.
 * Swiper is loaded on demand from the jsDelivr CDN so there is no hard
 * dependency on a bundled asset.
 *
 * Configuration is read from data-cc-* attributes written by the
 * custom-carousel-content.liquid snippet onto each carousel's wrapper div.
 *
 * This script may be included multiple times on the same page (once per
 * section that contains a carousel). Each inclusion calls initAll(), which
 * skips wrappers that already have data-cc-initialized set.  Swiper itself
 * is only fetched once thanks to the cached _swiperPromise.
 */

(function () {
  'use strict';

  const BREAKPOINT = 750; // px — matches Shopify Focal theme breakpoint
  const SWIPER_CSS = 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.css';
  const SWIPER_JS  = 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.js';

  // ─── Swiper loader (cached promise, shared across all script inclusions) ────

  window.__ccSwiperPromise = window.__ccSwiperPromise || null;

  function loadSwiper() {
    if (window.__ccSwiperPromise) return window.__ccSwiperPromise;

    window.__ccSwiperPromise = new Promise((resolve, reject) => {
      if (window.Swiper) { resolve(window.Swiper); return; }

      // Inject stylesheet if not already present.
      if (!document.querySelector(`link[href="${SWIPER_CSS}"]`)) {
        const link = document.createElement('link');
        link.rel  = 'stylesheet';
        link.href = SWIPER_CSS;
        document.head.appendChild(link);
      }

      // Inject script if not already present.
      const existing = document.querySelector(`script[src="${SWIPER_JS}"]`);
      if (existing) {
        existing.addEventListener('load',  () => resolve(window.Swiper));
        existing.addEventListener('error', reject);
        return;
      }

      const script   = document.createElement('script');
      script.src     = SWIPER_JS;
      script.addEventListener('load',  () => resolve(window.Swiper));
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });

    return window.__ccSwiperPromise;
  }

  // ─── Arrow visibility ───────────────────────────────────────────────────────

  function setArrowVisibility(prev, next, visible) {
    if (prev) prev.hidden = !visible;
    if (next) next.hidden = !visible;
  }

  // ─── Per-carousel initialisation ───────────────────────────────────────────

  function initCarousel(wrapper) {
    // Mark as initialized immediately so concurrent calls don't double-init.
    wrapper.setAttribute('data-cc-initialized', 'true');

    const swiperEl = wrapper.querySelector('.custom-carousel__swiper');
    const prevBtn  = wrapper.querySelector('.custom-carousel__prev');
    const nextBtn  = wrapper.querySelector('.custom-carousel__next');
    if (!swiperEl) return;

    // Read config from data attributes set by the Liquid snippet.
    const itemsMobile      = parseFloat(wrapper.dataset.ccItemsMobile)   || 1;
    const itemsDesktop     = parseFloat(wrapper.dataset.ccItemsDesktop)  || 3;
    const loop             = wrapper.dataset.ccLoop          === 'true';
    const autoplayEnabled  = wrapper.dataset.ccAutoplay      === 'true';
    const autoplaySpeed    = parseFloat(wrapper.dataset.ccAutoplaySpeed) || 4000;
    const showDots         = wrapper.dataset.ccDots          === 'true';
    const desktopArrows    = wrapper.dataset.ccDesktopArrows === 'true';
    const mobileArrows     = wrapper.dataset.ccMobileArrows  === 'true';
    const desktopDraggable = wrapper.dataset.ccDesktopDraggable === 'true';
    const mobileDraggable  = wrapper.dataset.ccMobileDraggable  === 'true';
    const gapMobile        = parseFloat(wrapper.dataset.ccGapMobile)   || 16;
    const gapDesktop       = parseFloat(wrapper.dataset.ccGapDesktop)  || 24;
    const transitionSpeed  = parseFloat(wrapper.dataset.ccSpeed)       || 600;

    const isDesktop  = window.matchMedia(`(min-width: ${BREAKPOINT}px)`).matches;
    const isMiniCart = !!wrapper.closest('#mini-cart');

    const config = {
      // ── Fixed options ──────────────────────────────────────────────────
      speed:                    transitionSpeed,
      centerInsufficientSlides: true,
      snapToSlideEdge:          true,
      roundLengths:             true,
      // Watch DOM and parent changes so Swiper re-measures if layout shifts
      // after the async CDN load resolves.
      observer:                 true,
      observeParents:           true,

      // ── From block settings ────────────────────────────────────────────
      loop,
      // Mini-cart: 'auto' below 460 px so slides size themselves via CSS.
      slidesPerView:   isMiniCart ? 'auto' : itemsMobile,
      spaceBetween:    gapMobile,
      allowTouchMove:  isDesktop ? desktopDraggable : mobileDraggable,

      breakpoints: {
        ...(isMiniCart ? {
          460: {
            slidesPerView: itemsMobile,
            spaceBetween:  '12px',
          },
        } : {}),
        [BREAKPOINT]: {
          slidesPerView:  itemsDesktop,
          spaceBetween:   gapDesktop,
          allowTouchMove: desktopDraggable,
        },
      },

      navigation: {
        prevEl: prevBtn,
        nextEl: nextBtn,
        disabledClass: 'custom-carousel__nav--disabled',
      },

      pagination: showDots
        ? { el: wrapper.querySelector('.swiper-pagination'), clickable: true }
        : false,

      autoplay: autoplayEnabled
        ? { delay: autoplaySpeed, disableOnInteraction: false, pauseOnMouseEnter: true }
        : false,
    };

    const swiper = new window.Swiper(swiperEl, config);
    wrapper._swiperInstance = swiper;

    // Apply initial arrow visibility for the current viewport.
    setArrowVisibility(prevBtn, nextBtn, isDesktop ? desktopArrows : mobileArrows);

    // Update arrow visibility and touch-move on viewport change.
    const mq = window.matchMedia(`(min-width: ${BREAKPOINT}px)`);
    const onViewportChange = (e) => {
      setArrowVisibility(prevBtn, nextBtn, e.matches ? desktopArrows : mobileArrows);
      swiper.params.allowTouchMove = e.matches ? desktopDraggable : mobileDraggable;
      swiper.update();
    };
    mq.addEventListener('change', onViewportChange);
  }

  // ─── Init all uninitialised carousels ───────────────────────────────────────

  function initAll() {
    document
      .querySelectorAll('[data-cc-items-mobile]:not([data-cc-initialized])')
      .forEach((wrapper) => {
        // Mini-cart carousels are initialized lazily when the drawer opens.
        // Never initialize them while hidden — Swiper measures zero dimensions.
        if (wrapper.closest('#mini-cart')) return;
        initCarousel(wrapper);
      });
  }

  // Run for any carousels already in the DOM when this script inclusion executes.
  loadSwiper()
    .then(initAll)
    .catch((err) => console.error('[custom-carousel] Failed to load Swiper:', err));

  // Also handle Shopify theme editor section reloads (only register once).
  if (!window.__ccSectionListener) {
    window.__ccSectionListener = true;
    document.addEventListener('shopify:section:load', () => {
      loadSwiper()
        .then(initAll)
        .catch((err) => console.error('[custom-carousel] Failed to load Swiper:', err));
    });
  }

  // Initialize carousels inside the mini-cart lazily — only when the drawer opens.
  // initAll() intentionally skips #mini-cart so Swiper never measures hidden elements.
  if (!window.__ccMiniCartListener) {
    window.__ccMiniCartListener = true;

    let _pvcRefreshTimer = null;

    function doMiniCartRefresh() {
      const miniCart = document.getElementById('mini-cart');
      if (!miniCart || !miniCart.hasAttribute('open')) return;

      // Debounce: if observer fires rapidly, only run once.
      clearTimeout(_pvcRefreshTimer);
      _pvcRefreshTimer = setTimeout(() => {
        loadSwiper().then(() => {
          // Destroy any previously initialized instances so Swiper re-measures.
          miniCart.querySelectorAll('[data-cc-items-mobile]').forEach((wrapper) => {
            if (wrapper._swiperInstance) {
              wrapper._swiperInstance.destroy(true, true);
              wrapper._swiperInstance = null;
            }
            wrapper.removeAttribute('data-cc-initialized');
          });
          // Double rAF: ensures the drawer's layout is fully painted before Swiper measures.
          requestAnimationFrame(() => requestAnimationFrame(() => {
            miniCart
              .querySelectorAll('[data-cc-items-mobile]:not([data-cc-initialized])')
              .forEach(initCarousel);
          }));
        }).catch(() => {});
      }, 50);
    }

    function attachMiniCartObserver() {
      const miniCart = document.getElementById('mini-cart');
      if (!miniCart) return;
      // Handle cart already open at attach time.
      doMiniCartRefresh();
      // Watch for open/close AND for innerHTML replacement by _rerenderCart
      // (which replaces direct children of cart-drawer after cart updates).
      new MutationObserver(() => doMiniCartRefresh())
        .observe(miniCart, { attributes: true, attributeFilter: ['open'], childList: true });
      // Re-init after full page load (theme may re-render cart HTML on load).
      window.addEventListener('load', () => doMiniCartRefresh(), { once: true });
      // Re-init whenever the cart content is updated (re-rendered by theme.js).
      document.documentElement.addEventListener('cart:updated', () => doMiniCartRefresh());
    }

    // Attach immediately if mini-cart is already in the DOM, otherwise wait.
    if (document.getElementById('mini-cart')) {
      attachMiniCartObserver();
    } else {
      document.addEventListener('DOMContentLoaded', attachMiniCartObserver);
    }
  }

})();
